import * as THREE from 'three';
import { CONFIG } from '../../config.js';
import { BINARY_DEFAULTS } from './binaryConfig.js';

const vertexShader = /* glsl */ `
attribute float aCellId;
attribute float aPhase;
attribute float aBrightness;

varying vec2 vUv;
varying float vCellId;
varying float vPhase;
varying float vBrightness;

void main() {
  vUv = uv;
  vCellId = aCellId;
  vPhase = aPhase;
  vBrightness = aBrightness;

  vec4 worldPosition = instanceMatrix * vec4(position, 1.0);
  vec4 mvPosition = modelViewMatrix * worldPosition;
  gl_Position = projectionMatrix * mvPosition;
}
`;

const fragmentShader = /* glsl */ `
uniform float uTime;
uniform sampler2D uAtlas;
uniform vec3 uColorBase;
uniform vec3 uColorGlow;
uniform float uFlipSpeed;
uniform float uScrollSpeed;
uniform float uFlickerSpeed;
uniform float uFlickerStrength;
uniform float uPulseSpeed;
uniform float uGlowStrength;
uniform float uColumns;
uniform float uEventBoost;

varying vec2 vUv;
varying float vCellId;
varying float vPhase;
varying float vBrightness;

void main() {
  float col = mod(vCellId, uColumns);
  float row = floor(vCellId / uColumns);

  float shift = floor(uTime * uScrollSpeed * (1.0 + uEventBoost * 1.6) + vPhase * 3.0);
  float stream = mod(col + shift + row, 2.0);

  float tick = floor(uTime * uFlipSpeed * (1.0 + uEventBoost * 3.5) + vCellId * 0.37 + vPhase * 11.0);
  float alternate = mod(tick, 2.0);
  float digit = mod(stream + alternate, 2.0);

  vec2 atlasUv = vec2(vUv.x * 0.5 + digit * 0.5, vUv.y);
  vec4 glyph = texture2D(uAtlas, atlasUv);
  float glyphMask = max(glyph.r, max(glyph.g, glyph.b));

  float flicker = 1.0 - uFlickerStrength
    + uFlickerStrength * sin(uTime * uFlickerSpeed * (1.0 + uEventBoost * 2.0) + vPhase * 6.2831853);
  float pulse = 0.82 + 0.18 * sin(uTime * uPulseSpeed + vCellId * 0.11);
  float intensity = vBrightness * flicker * pulse * (1.0 + uEventBoost * 0.35);

  vec3 emissive = uColorGlow * glyphMask * intensity * uGlowStrength;
  vec3 base = uColorBase * (1.0 - glyphMask * 0.65);
  vec3 color = base + emissive;

  gl_FragColor = vec4(color, 1.0);
}
`;

/**
 * Reusable ShaderMaterial for instanced binary glyph quads.
 * Approach: texture atlas + InstancedMesh + custom GLSL (GPU digit animation).
 */
export class BinaryMaterial {
  /**
   * @param {{
   *   atlas: import('./BinaryAtlas.js').BinaryAtlas;
   *   columns?: number;
   *   overrides?: Partial<typeof BINARY_DEFAULTS>;
   *   palette?: 'building' | 'accent';
   * }} options
   */
  constructor({ atlas, columns = 32, overrides = {}, palette = 'building' }) {
    this.settings = { ...BINARY_DEFAULTS, ...overrides };
    this.columns = columns;
    this.time = 0;
    this.palette = palette;
    this._eventBoost = 0;

    const matrix = CONFIG.matrix;
    const baseHex = palette === 'building' ? matrix.darkGreen : matrix.darkRed;
    const glowHex = palette === 'building' ? matrix.primaryGreen : matrix.primaryRed;
    const glowColor = new THREE.Color(glowHex);
    if (palette === 'building') {
      glowColor.multiplyScalar(1.06);
    } else if (palette === 'accent') {
      glowColor.multiplyScalar(1.12);
    }
    const glowStrength =
      palette === 'building'
        ? (overrides.glowStrength ?? BINARY_DEFAULTS.buildingGlowStrength)
        : (overrides.glowStrength ?? this.settings.glowStrength);
    const scrollSpeed =
      palette === 'building'
        ? (overrides.scrollSpeed ?? BINARY_DEFAULTS.buildingScrollSpeed)
        : this.settings.scrollSpeed;
    this.threeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uAtlas: { value: atlas.texture },
        uColorBase: { value: new THREE.Color(baseHex) },
        uColorGlow: { value: glowColor },
        uFlipSpeed: { value: this.settings.flipSpeed },
        uScrollSpeed: { value: scrollSpeed },
        uFlickerSpeed: { value: this.settings.flickerSpeed },
        uFlickerStrength: { value: this.settings.flickerStrength },
        uPulseSpeed: { value: this.settings.pulseSpeed },
        uGlowStrength: { value: glowStrength },
        uColumns: { value: columns },
        uEventBoost: { value: 0 },
      },
      vertexShader,
      fragmentShader,
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide,
    });
  }

  setColumns(columns) {
    this.columns = columns;
    this.threeMaterial.uniforms.uColumns.value = columns;
  }

  /** @param {number} deltaSeconds */
  update(deltaSeconds) {
    this.time += deltaSeconds;
    this.threeMaterial.uniforms.uTime.value = this.time;
  }

  /** @param {number} boost 0–1 event intensity multiplier */
  setEventBoost(boost) {
    this._eventBoost = boost;
    this.threeMaterial.uniforms.uEventBoost.value = boost;
  }

  /** @param {number} strength */
  setFlickerStrength(strength) {
    this.threeMaterial.uniforms.uFlickerStrength.value = strength;
  }

  dispose() {
    this.threeMaterial.dispose();
  }
}
